import {
  all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findMatch,
  firstSelected,
  successAndErrorsOnDestroy,
} from '@/utils/resultsHelper';
import selectors from '@/selectors';
import toggleAdvisoryOnCondition from '../helper';
import {
  fetchDatasetConditionsError,
  fetchDatasetConditionsSuccess,
  updateDatasetConditionError,
  updateDatasetConditionSuccess,
  removeDatasetConditionError,
  removeDatasetConditionSuccess,
  fetchConditionsSuccess,
  fetchConditionsError,
  addDatasetConditionSuccess,
  addDatasetConditionError,
  toggleDatasetConditionAdvisoryError,
  showDatasetConditionsError,
  showDatasetConditionsSuccess,
  downloadDatasetConditionAttachmentError,
  downloadDatasetConditionAttachmentSuccess,
} from '../actions';
import actions, { conditionsActions } from '../constants';
import ConditionsService from '../service';

export function* conditions() {
  const { response, error } = yield call(callAdapter, ConditionsService.conditions());
  if (error) {
    yield put(fetchConditionsError(error));
  } else {
    yield put(fetchConditionsSuccess(response.items));
  }
}

export function* datasetConditions({ category, code }) {
  const { response, error } = yield call(callAdapter, ConditionsService.datasetConditions(null, code));
  if (error) {
    yield put(fetchDatasetConditionsError(category, code, error));
  } else {
    yield put(fetchDatasetConditionsSuccess(category, code, response.items));
  }
}

export function* activeDatasetConditions({ entity, code }) {
  const { response, error } = yield call(callAdapter, ConditionsService.datasetConditions(entity, code));
  if (error) {
    yield put(showDatasetConditionsError(error));
  } else {
    yield put(showDatasetConditionsSuccess(response.items));
  }
}

export function* create() {
  const {
    code, category, items, advisory, name,
  } = yield select(selectors.activeDatasetCondition);
  const datasetCondition = firstSelected(items);

  const { response, error } = yield call(
    callAdapter,
    ConditionsService.create(code, datasetCondition, advisory),
  );

  if (error) {
    yield put(addDatasetConditionError(name, error));
  } else {
    yield put(addDatasetConditionSuccess(category, code, response, name));
  }
}

export function* update() {
  const {
    code, category, items, advisory, name,
  } = yield select(selectors.activeDatasetCondition);
  const datasetCondition = firstSelected(items);

  const { response, error } = yield call(
    callAdapter,
    ConditionsService.update(code, datasetCondition, advisory),
  );

  if (error) {
    yield put(updateDatasetConditionError(name, error));
  } else {
    yield put(updateDatasetConditionSuccess(category, code, response, name));
  }
}

export function* toggleAdvisory({
  category,
  code,
  datasetConditionId,
  name,
}) {
  const items = yield select(selectors.datasetConditions);
  const item = findResult(items, category, code);
  const datasetCondition = findMatch(item.conditions, datasetConditionId, 'id');

  const { response, error } = yield call(
    callAdapter,
    ConditionsService.toggleAdvisory(code, toggleAdvisoryOnCondition(datasetCondition)),
  );

  if (error) {
    yield put(toggleDatasetConditionAdvisoryError(category, code, datasetConditionId, error, name));
  } else {
    yield put(updateDatasetConditionSuccess(category, code, response, name));
  }
}

export function* destroy(action) {
  const results = yield all(action.items.map((i) => callAdapterResponseError(i, ConditionsService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Dataset or Condition not found');

  if (errors.length > 0) {
    yield put(removeDatasetConditionError(action.name, errors));
  }
  if (success.length > 0) {
    yield put(removeDatasetConditionSuccess(action.name, success));
  }
}

export function* downloadAttachment({ datasetCondition, datasetCode }) {
  const { response, error } = yield call(
    callAdapter,
    ConditionsService.attachment({ code: datasetCode, id: datasetCondition.id }),
  );

  if (response) {
    saveAs(
      new Blob([response], {
        type: datasetCondition.context.attachmentType,
      }),
      datasetCondition.context.attachmentName,
    );

    yield put(downloadDatasetConditionAttachmentSuccess(datasetCode, datasetCondition, response));

    return;
  }

  yield put(downloadDatasetConditionAttachmentError(datasetCode, datasetCondition, error));
}

export default function* datasetConditionsApi() {
  yield takeEvery(conditionsActions.FETCH_CONDITIONS, conditions);
  yield takeEvery(actions.FETCH_DATASET_CONDITIONS, datasetConditions);
  yield takeLatest(actions.SHOW_DATASET_CONDITIONS, activeDatasetConditions);
  yield takeLatest(actions.ADD_DATASET_CONDITION, create);
  yield takeLatest(actions.UPDATE_DATASET_CONDITION, update);
  yield takeLatest(actions.REMOVE_DATASET_CONDITION, destroy);
  yield takeEvery(actions.TOGGLE_DATASET_CONDITION_ADVISORY, toggleAdvisory);
  yield takeEvery(actions.DOWNLOAD_DATASET_CONDITION_ATTACHMENT, downloadAttachment);
}
